<template>
    <div class="view view-practice-modes">
        <HeaderPracticeMode />
        <Preloader
            v-if="showPreloader"
            preloaderText="Ortam hazırlanıyor..."/>
        <Swiper
            v-if="!showPreloader"
            :modules="swiperModules"
            :pagination="{ clickable: true }"
            :slidesPerView="1"
            @swiper="updateSelectedMode"
            @slideChange="updateSelectedMode">
            <SwiperSlide v-for="mode in practiceModes" :key="mode.id">
                <PracticeMode
                    :modeId="mode.id"
                    :modeType="mode.type"
                    :modeSlug="mode.slug"
                    :modeName="mode.name"
                    :modeDesc="mode.description" />
            </SwiperSlide>
        </Swiper>
        <Navbar
            @navlinkClick="navlinkClick" />
        <BtnChallenge @click="routeSelectUser" />
        <BtnPracticeStart
            @click="this.$store.state.envDev ? initPractice() : initPracticeApi()"/>
    </div>
</template>

<script>
    /*
        +---+---+---+--+---+--+---+---+---+---+---+

                    Library Imports

        +---+---+---+--+---+--+---+---+---+---+---+
    */
    import FakeApi from '@/libs/FakeApi.js';




    /*
        +---+---+---+--+---+--+---+---+---+---+---+
    
                    Component Imports
    
        +---+---+---+--+---+--+---+---+---+---+---+
    */
    import HeaderPracticeMode   from '@/components/HeaderPracticeMode.vue';
    import Preloader            from '@/components/Preloader.vue';
    import BtnChallenge         from '@/components/BtnChallenge.vue';
    import PracticeMode         from '@/components/PracticeMode.vue';
    import Navbar               from '@/components/Navbar.vue';
    import BtnPracticeStart     from '@/components/BtnPracticeStart';
    import { Swiper }           from 'swiper/vue';
    import { SwiperSlide }      from 'swiper/vue';
    import 'swiper/css';
    import { Pagination }       from 'swiper';
    



    /*
        +---+---+---+--+---+--+---+---+---+---+---+
    
                    Component Instance
    
        +---+---+---+--+---+--+---+---+---+---+---+
    */
    export default {
        components: {
            HeaderPracticeMode,
            PracticeMode,
            Navbar,
            BtnChallenge,
            BtnPracticeStart,
            Swiper,
            SwiperSlide,
            Preloader,
        },

        data(){
            return {
                swiperModules: [Pagination],
                selectedMode: null,
                showPreloader: false,
                canInit: true,
                abortController: false,
                allow: {
                    updateSelectedMode: true,
                }
            };
        },

        computed: {
            practiceModes(){
                return this.$store.state.practiceModes;
            }
        },

        methods: {

            updateSelectedMode(swiper){
                // When routing to practice mode views, swiper runs this callback
                // one more time and the selected mode changes to first mode which causes
                // restart button in practice results view to always restart in 1st mode. This prevents that.
                if(this.allow.updateSelectedMode == false){
                    return;
                }
                let curIdx = swiper.activeIndex;
                this.$store.commit('setSelectedPracticeMode', this.practiceModes[curIdx]);
                this.$store.commit('setInput', {
                    categoryLabel: 'initializePractice',
                    dataLabel: 'practiceType',
                    data: this.practiceModes[curIdx].type,
                });
            },

            initPractice(){

                // Check if user can init practice to avoid multiple click requests.
                if(this.canInit !== true){
                    return;
                }
                this.canInit = false;
                this.allow.updateSelectedMode = false;
                
                // Enable preloader.
                this.showPreloader = true;

                // Prepare practice options payload.
                let practiceOptions = {
                    practiceType: this.$store.state.selectedPracticeMode.type,
                };

                let fakeApi = new FakeApi();
                fakeApi.initPractice(JSON.stringify(practiceOptions))
                .then(data => {
                    console.log(data);
                    
                    // Disable preloader.
                    this.showPreloader = false;

                    // Set practice session Vuex state.
                    this.$store.commit('setPracticeSession', data.practiceSession);

                    // Set question set Vuex state.
                    this.$store.commit('setQuestionSet', data.questionSet);

                    // Redirect to related practice view.
                    this.$router.push(`/practice/${this.$store.state.selectedPracticeMode.type}`);
                })
                .catch(err => {
                    console.error(err);
                    this.$foneturkError({
                        text: 'Bir şeyler ters gitti.'
                    });
                    this.showPreloader = false;
                    this.allow.updateSelectedMode = true;
                });

            },

            initPracticeApi(){

                // Check if user can init practice to avoid multiple click requests.
                if(this.canInit !== true){
                    return;
                }
                this.canInit = false;
                this.allow.updateSelectedMode = false;

                // Enable preloader.
                this.showPreloader = true;

                // Prepare practice options request body.
                let practiceOptions = {
                    practiceType: this.$store.state.input.initializePractice.practiceType,
                };
                let requestBody = 'practiceOptions=' + JSON.stringify(practiceOptions);
                this.abortController = new AbortController();

                // Send API request to backend.
                fetch(this.$store.state.api.initializePractice, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: requestBody,
                    signal: this.abortController.signal,
                })
                .then(async res => {
                    if(res.status !== 200){
                        throw await res.json();
                    }

                    return res.json();
                })
                .then(practiceData => {
                    console.log(practiceData);
                    
                    // Disable preloader.
                    this.showPreloader = false;

                    // Set practice session Vuex state.
                    this.$store.commit('setPracticeSession', practiceData.practiceSession);

                    // Set question set Vuex state.
                    this.$store.commit('setQuestionSet', practiceData.questionSet);

                    // Redirect to related practice view.
                    this.$router.push(`/practice/${this.$store.state.input.initializePractice.practiceType}`);
                })
                .catch(err => {
                    console.error(err);
                    if(err == 'VIEW_PRACTICE_MODES/NAVLINK_CLICK::ABORT_REQUEST'){
                        return;
                    }
                    if(err instanceof Error){
                        console.error(`PRACTICE_MODES_VIEW/INIT_PRACTICE_API::FETCH_ERROR`);
                        this.$foneturkError({
                            text: this.$t(`error.fetch`),
                        });
                    }
                    else if(err.code === 'INITIALIZE_PRACTICE_CONTROLLER/INDEX::INV_USER_SESSION'){
                        console.error(`PRACTICE_MODES_VIEW/INIT_PRACTICE_API::API_ERROR_01 --> ${err.code}`);
                        this.$foneturkError({
                            text: this.$t(`error.api.initializePractice.${err.code}`),
                            didClose: () => {
                                this.$router.push('/login');
                            }
                        });
                    }
                    else if(this.$te(`error.api.initializePractice.${err.code}`)){
                        console.error(`PRACTICE_MODES_VIEW/INIT_PRACTICE_API::API_ERROR_02 --> ${err.code}`);
                        this.$foneturkError({
                            text: this.$t(`error.api.initializePractice.${err.code}`),
                        });
                    }
                    else{
                        console.error(`PRACTICE_MODES_VIEW/INIT_PRACTICE_API::UNEXPECTED_API_ERROR --> ${err.code}`);
                        this.$foneturkError({
                            text: this.$t(`error.fetch`),
                        });
                    }
                    
                    this.showPreloader = false;
                    this.canInit = true;
                    this.allow.updateSelectedMode = true;
                });
            },

            navlinkClick(){
                if(this.abortController){
                    this.abortController.abort("VIEW_PRACTICE_MODES/NAVLINK_CLICK::ABORT_REQUEST");
                    this.showPreloader = false;
                    this.canInit = true;
                }
            },

            routeSelectUser(){
                this.$router.push('/challenge/select-user');
            },
        },
    };
</script>

<style scoped src="@/css/views/view-practice-modes.css"></style>